/**
 * The permission grid (الصلاحيات): a row per screen, a column per action
 * (view / add / edit / delete) and the screen's extra rights (export,
 * print, post charges, restore...) beside them, with "All" / "None".
 *
 * Ticking anything on a screen ticks its View too - adding to a list you
 * cannot open is not a thing - and unticking View clears the row. The
 * server keeps the same rule (App\Support\Permissions).
 *
 * Used twice: inside the Add / Edit User form as soon as the role is not
 * a customer (PermissionsField), and in the Permissions window on a staff
 * row of the Users list.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";

const CATALOG = "https://trust-cargo.co/anas_backend/api/v2/permissions/catalog";
const COLUMNS = ["view", "create", "update", "delete"];
const HEADS = {
  view: "عرض · View",
  create: "إضافة · Add",
  update: "تعديل · Edit",
  delete: "حذف · Delete",
};

/** Every key on one screen's row. */
export const rowKeys = (m) => [
  ...m.actions.map((a) => `${m.module}.${a.key}`),
  ...m.extras.map((x) => `${m.module}.${x.key}`),
];

/** Tick or untick one key, keeping View in step with the rest of its row. */
export function toggle(picked, catalog, key, on) {
  const [module, what] = key.split(".");
  const m = catalog.find((c) => c.module === module);
  const next = new Set(picked);
  if (!on) {
    next.delete(key);
    // no View, nothing else on that screen
    if (what === "view" && m) rowKeys(m).forEach((k) => next.delete(k));
  } else {
    next.add(key);
    if (m && m.actions.some((a) => a.key === "view")) next.add(`${module}.view`);
  }
  return [...next];
}

function PermissionsGrid({ catalog, value, onChange }) {
  const picked = Array.isArray(value) ? value : [];
  const has = (k) => picked.includes(k);
  const set = (k, on) => onChange(toggle(picked, catalog, k, on));
  const allKeys = catalog.flatMap(rowKeys);

  const setRow = (m, on) => {
    const rest = picked.filter((k) => !k.startsWith(`${m.module}.`));
    onChange(on ? [...rest, ...rowKeys(m)] : rest);
  };

  return (
    <SuiBox>
      <SuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <SuiTypography variant="button" color="text" sx={{ flex: 1 }}>
          {`${picked.length} / ${allKeys.length} صلاحية · permissions ticked`}
        </SuiTypography>
        <Button size="small" variant="outlined" color="dark" onClick={() => onChange(allKeys)}>
          كل الصلاحيات · All
        </Button>
        <Button size="small" variant="outlined" color="dark" onClick={() => onChange([])}>
          ولا إشي · None
        </Button>
      </SuiBox>
      <SuiBox sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 820 }} aria-label="permissions grid">
          <TableHead>
            <TableRow>
              <TableCell>الشاشة · Screen</TableCell>
              {COLUMNS.map((c) => (
                <TableCell key={c} align="center">
                  {HEADS[c]}
                </TableCell>
              ))}
              <TableCell>إضافي · Extra</TableCell>
              <TableCell align="center">الكل · Row</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {catalog.map((m) => {
              const keys = rowKeys(m);
              const full = keys.every(has);
              return (
                <TableRow key={m.module} hover>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
                    {m.label}
                  </TableCell>
                  {COLUMNS.map((c) => {
                    const k = `${m.module}.${c}`;
                    return (
                      <TableCell key={c} align="center">
                        {m.actions.some((a) => a.key === c) ? (
                          <Checkbox
                            size="small"
                            checked={has(k)}
                            onChange={(e) => set(k, e.target.checked)}
                            inputProps={{ "aria-label": `${m.module} ${c}` }}
                          />
                        ) : (
                          <SuiBox component="span" sx={{ opacity: 0.3 }}>
                            —
                          </SuiBox>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <SuiBox display="flex" flexWrap="wrap" columnGap={1}>
                      {m.extras.map((x) => {
                        const k = `${m.module}.${x.key}`;
                        return (
                          <FormControlLabel
                            key={k}
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={has(k)}
                                onChange={(e) => set(k, e.target.checked)}
                                inputProps={{ "aria-label": `${m.module} ${x.key}` }}
                              />
                            }
                            label={<SuiTypography sx={{ fontSize: 12 }}>{x.label}</SuiTypography>}
                          />
                        );
                      })}
                    </SuiBox>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={full}
                      indeterminate={!full && keys.some(has)}
                      onChange={(e) => setRow(m, e.target.checked)}
                      inputProps={{ "aria-label": `${m.module} all` }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </SuiBox>
    </SuiBox>
  );
}

PermissionsGrid.defaultProps = { value: [] };

PermissionsGrid.propTypes = {
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      module: PropTypes.string,
      label: PropTypes.string,
      actions: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string })),
      extras: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string })),
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

/**
 * The grid as a field of the user form: reads the list of screens itself,
 * and says so instead for an admin - who can do everything anyway.
 */
export function PermissionsField({ role, value, onChange }) {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(CATALOG, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
        },
      })
      .then((r) => setCatalog(Array.isArray(r.data) ? r.data : []))
      .catch((e) => {
        setCatalog([]);
        setError(
          e?.response?.status === 401
            ? "انتهت جلسة الدخول. سجّل خروج وادخل من جديد عشان تحدد الصلاحيات."
            : "ما قدرنا نجيب قائمة الصلاحيات من السيرفر · Could not load the permissions list"
        );
      });
  }, []);

  return (
    <SuiBox
      component="fieldset"
      sx={({ palette, borders, functions: { rgba } }) => ({
        m: 0,
        px: 2,
        pt: 0.5,
        pb: 1.5,
        minWidth: 0,
        border: `1.5px solid ${rgba(palette.dark.main, 0.2)}`,
        borderRadius: borders.borderRadius.md,
        "& legend": { px: 0.75, fontSize: 12, fontWeight: 700 },
      })}
    >
      <legend>الصلاحيات · Permissions</legend>
      {role === "admin" && (
        <Alert severity="info">
          الأدمن عنده كل الصلاحيات دايماً. لتحديد صلاحيات معيّنة اختار دور غير admin (مثلاً
          employee). · An admin can do everything - pick another role to choose permissions.
        </Alert>
      )}
      {role !== "admin" && catalog === null && <CargoLoader />}
      {role !== "admin" && error && <Alert severity="warning">{error}</Alert>}
      {role !== "admin" && catalog && catalog.length > 0 && (
        <PermissionsGrid catalog={catalog} value={value} onChange={onChange} />
      )}
    </SuiBox>
  );
}

PermissionsField.defaultProps = { role: "", value: [] };

PermissionsField.propTypes = {
  role: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

export default PermissionsGrid;
